import { ItemClasses, PricingType, PricingTypes } from "../../common/constants";
import { CompletedWorkareaVisit } from "../../domain/invoicing/invoicingApi";
import { sum } from "../../helpers/array";
import { roundToPrecision } from "../../helpers/math";
import { hasValue } from "../../helpers/nullable";
import { ViewItem, calculateTotalStandardPrice } from "./itemModel";

type Summary = {
  materialsCostPlusTax: number
  grossProfit: number
  recovery: number
}

export const calculateEstimateSummary = (
  viewItems: ViewItem[],
  quotedPrice: number | undefined,
  estimatedOccurrences: number | undefined,
  pricingType: PricingType,
  recovery = 0
): Summary => {
  const notDeleted = viewItems.filter(x => !x.isDeleted)
  const totalPrice = sum(notDeleted, x => x.totalPrice)
  let workareaPrice = quotedPrice ?? totalPrice
  if (pricingType === PricingTypes.QuotedPrice && estimatedOccurrences && hasValue(quotedPrice)) {
    workareaPrice = Math.floor(workareaPrice / estimatedOccurrences)
    recovery = recovery / estimatedOccurrences
  }

  return calculateSummary(workareaPrice, notDeleted, recovery)
}

export const calculateActualSummary = (viewItems: ViewItem[], quotedPrice: number | undefined, pricingType: PricingType, completedWorkareaVisits:CompletedWorkareaVisit[], workareaId: number, recovery = 0): Summary => {
  const notDeleted = viewItems.filter(x => !x.isDeleted)
  const totalStandardPrice = sum(notDeleted, calculateTotalStandardPrice)
  const workareaPrice = pricingType === PricingTypes.PerVisit
    ? sum(completedWorkareaVisits.filter(x => x.workareaId === workareaId), x => x.visitPrice ?? 0)
    : quotedPrice ?? totalStandardPrice
  const actualRecovery = pricingType === PricingTypes.PerVisit
    ? sum(completedWorkareaVisits.filter(x => x.workareaId === workareaId), x => x.visitRecovery ?? 0)
    : recovery
  return calculateSummary(workareaPrice, notDeleted, actualRecovery)
}

const calculateSummary = (workareaPrice: number, items: ViewItem[], recovery: number): Summary => {
  const materialsCostPlusTax = sum(items.filter(x => x.class === ItemClasses.Materials), x => x.costPlusTax * x.quantity)

  const totalCostPlusTax = sum(items, x => x.costPlusTax * x.quantity)
  const workPrice = workareaPrice - recovery
  const grossProfit = workPrice === 0 ? 0 : roundToPrecision((workPrice - totalCostPlusTax) / workPrice, 4)

  return {
    materialsCostPlusTax,
    grossProfit,
    recovery: workareaPrice - workPrice,
  }
}
